import { type QueryClient, useQuery } from "@tanstack/react-query";
import { createRootRouteWithContext, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { CompassIcon, TriangleAlertIcon } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Toaster } from "sonner";
import { AppShell } from "@/components/app/app-shell";
import { CommandPaletteProvider } from "@/components/app/command-palette";
import { EmptyState } from "@/components/app/empty-state";
import { GarageDeactivated, GarageNotFound } from "@/components/app/garages";
import { Page } from "@/components/app/page";
import { Button } from "@/components/ui/button";
import {
  GarageRecoveryKeyBanner,
  NewPasswordScreen,
  NoKeyScreen,
  UnlockScreen,
  useNewPasswordSkip,
  useRecoveryKeyOffer,
} from "@/features/keys/keys";
import { endRound } from "@/features/labels/scanner/round";
import { setLanguage } from "@/i18n";
import { encryptsGarages, signsIn, useMe } from "@/lib/account";
import { CurrentVehicleProvider } from "@/lib/current-vehicle";
import { errorMessage } from "@/lib/errors";
import { garage, rememberGarage } from "@/lib/garage";
import { useTheme } from "@/lib/theme";
import { useTRPC } from "@/lib/trpc";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: RouteError,
});

function RootLayout() {
  const { theme } = useTheme();
  const trpc = useTRPC();
  const { data: garages } = useQuery(trpc.garages.list.queryOptions());
  const current = garages?.find((g) => g.name === garage);
  const exists = !garages || Boolean(current);
  // A platform admin deactivated it (ADR 0011): it stays listed, but opens for nobody.
  const deactivated = current?.deactivated === true;
  useEffect(() => {
    if (garages && exists && !deactivated && garage) rememberGarage(garage);
  }, [garages, exists, deactivated]);
  // Where one person uses the app, the garage's language, once chosen, is the app's; where people
  // sign in it shows only to someone who has no language of their own (architecture.md › Garage
  // settings).
  const { data: settings } = useQuery({
    ...trpc.settings.get.queryOptions(),
    enabled: Boolean(garage) && exists && !deactivated,
  });
  const { data: me, isPending: mePending } = useMe();
  const { data: access } = useQuery({
    ...trpc.garages.access.queryOptions(),
    enabled: Boolean(garage) && exists && !deactivated,
  });
  const keys = access?.keys;
  const keyOpen = exists && !deactivated && access?.kind === "member" && keys?.state === "open";
  useRecoveryKeyOffer(keyOpen);
  // Recovering with the key open (a passkey signed in after a reset): the new password, once.
  const { data: keyStatus } = useQuery({
    ...trpc.keys.status.queryOptions(),
    enabled: encryptsGarages() && keyOpen,
  });
  const [newPasswordSkipped, skipNewPassword] = useNewPasswordSkip();
  const language = settings?.language;
  const ownLanguage = signsIn() && (mePending || Boolean(me?.language));
  useEffect(() => {
    if (language && !ownLanguage) setLanguage(language);
  }, [language, ownLanguage]);

  // The scanner has its own minimal layout, without the sidebar (ADR 0018).
  const scanner = useRouterState({ select: (s) => /^\/qr(\/|$)/.test(s.location.pathname) });
  // Leaving the scanner ends its picking round.
  useEffect(() => {
    if (!scanner) endRound();
  }, [scanner]);

  if (!exists) return <GarageNotFound garages={garages ?? []} />;
  if (deactivated) return <GarageDeactivated garages={garages ?? []} />;
  // Its key must be open here before the garage shows (ADR 0022).
  if (encryptsGarages() && garage && !access) return null;
  if (keys?.state === "locked") return <UnlockScreen />;
  if (keyOpen && keyStatus?.passwordStale && !newPasswordSkipped) {
    return <NewPasswordScreen onSkip={skipNewPassword} />;
  }
  if (keys?.state === "noKey" || keys?.state === "keyOwed") {
    return <NoKeyScreen owed={keys.state === "keyOwed"} restorable={keys.restorable} />;
  }
  return (
    <CurrentVehicleProvider>
      <CommandPaletteProvider>
        {scanner ? (
          <Outlet />
        ) : (
          <AppShell>
            <GarageRecoveryKeyBanner due={Boolean(keys?.recoveryKeyDue)} />
            <Outlet />
          </AppShell>
        )}
        <Toaster
          theme={theme}
          position="bottom-right"
          offset={{ bottom: 24 }}
          mobileOffset={{ bottom: 84 }}
          toastOptions={{
            classNames: {
              toast: "!rounded-xl !border-border !bg-surface-raised !text-foreground !shadow-lg",
            },
          }}
        />
      </CommandPaletteProvider>
    </CurrentVehicleProvider>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <Page>
      <EmptyState
        icon={CompassIcon}
        title={t("errors.NOT_FOUND")}
        action={
          <Button asChild variant="outline">
            <Link to="/">{t("nav.overview")}</Link>
          </Button>
        }
        className="mt-10"
      />
    </Page>
  );
}

function RouteError({ error, reset }: { error: unknown; reset: () => void }) {
  const { t } = useTranslation();
  return (
    <Page>
      <EmptyState
        icon={TriangleAlertIcon}
        title={t("errors.pageTitle")}
        body={errorMessage(error, t)}
        action={
          <>
            <Button variant="outline" onClick={reset}>
              {t("common.retry")}
            </Button>
            <Button asChild variant="ghost">
              <Link to="/">{t("nav.overview")}</Link>
            </Button>
          </>
        }
        className="mt-10"
      />
    </Page>
  );
}
